import { memo } from 'react';
import {
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@flote/ui';
import { Users, Zap, UserCheck } from 'lucide-react';
import type { Team, Driver } from '@flote/contracts/http';

interface TeamDriverAssignmentProps {
  teams: Team[];
  selectedTeamId: string;
  onTeamChange: (teamId: string) => void;
  autoAssign: boolean;
  onAutoAssignChange: (autoAssign: boolean) => void;
  drivers: Driver[];
  selectedDriverId: string;
  onDriverChange: (driverId: string) => void;
  isLoadingDrivers: boolean;
  disabled?: boolean;
  noTeamValue: string;
  noDriverValue: string;
  showDriverSelect?: boolean;
}

export const TeamDriverAssignment = memo<TeamDriverAssignmentProps>(({
  teams,
  selectedTeamId,
  onTeamChange,
  autoAssign,
  onAutoAssignChange,
  drivers,
  selectedDriverId,
  onDriverChange,
  isLoadingDrivers,
  disabled = false,
  noTeamValue,
  noDriverValue,
  showDriverSelect = true,
}) => {
  const hasTeamSelected = selectedTeamId !== noTeamValue;
  const showDriverSelector = hasTeamSelected && !autoAssign && showDriverSelect;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Users className="h-4 w-4 text-muted-foreground" />
        Team Assignment
        <span className="text-xs font-normal text-muted-foreground">(optional)</span>
      </div>

      <div className="space-y-2">
        <Label htmlFor="teamId">Assign to Team</Label>
        <Select value={selectedTeamId} onValueChange={onTeamChange} disabled={disabled}>
          <SelectTrigger>
            <SelectValue placeholder="Select a team (optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={noTeamValue}>No team assigned</SelectItem>
            {teams.map((team) => (
              <SelectItem key={team.id} value={team.id}>
                {team.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Assign this task to a specific team for routing
        </p>
      </div>

      {hasTeamSelected && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="autoAssign"
            checked={autoAssign}
            onCheckedChange={(checked) => onAutoAssignChange(checked === true)}
            disabled={disabled}
          />
          <div className="flex items-center gap-1">
            <Zap className="h-3 w-3 text-muted-foreground" />
            <Label htmlFor="autoAssign" className="text-sm font-normal cursor-pointer">
              Auto-assign to driver
            </Label>
          </div>
        </div>
      )}

      {hasTeamSelected && autoAssign && (
        <p className="text-xs text-muted-foreground pl-6">
          Onfleet will automatically assign this task to an available driver on the team
        </p>
      )}

      {/* Driver Selection */}
      {showDriverSelector && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-muted-foreground" />
            <Label htmlFor="driverId">Assign to Driver</Label>
            <span className="text-xs font-normal text-muted-foreground">(optional)</span>
          </div>
          <Select
            value={selectedDriverId}
            onValueChange={onDriverChange}
            disabled={isLoadingDrivers || disabled}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={isLoadingDrivers ? 'Loading drivers...' : 'Select a driver (optional)'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={noDriverValue}>No driver assigned</SelectItem>
              {drivers.map((driver) => (
                <SelectItem key={driver.id} value={driver.id}>
                  {driver.name}
                  {driver.phone && (
                    <span className="ml-1 text-muted-foreground">({driver.phone})</span>
                  )}
                  {!driver.onDuty && (
                    <span className="ml-1 text-muted-foreground">(Off duty)</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {drivers.length === 0 && !isLoadingDrivers
              ? 'No drivers available for this team'
              : 'Manually assign this task to a specific driver'}
          </p>
        </div>
      )}
    </div>
  );
});

TeamDriverAssignment.displayName = 'TeamDriverAssignment';
